import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import {
  fetchEngineerLeaderboard,
  fetchEngineerLeaderboardByOrg,
  fetchOrganizationTeamsLeaderboard,
} from '@/apis/engineerLeaderboard';
import { fetchOrganizationLeaderboard } from '@/apis/organizationLeaderboard';
import { EngineerLeaderboardResponse } from '@/types/engineerLeaderboard';
import { OrganizationLeaderboardResponse } from '@/types/organizationLeaderboard';
import { normalizeOrgUUID } from '@/lib/orgIdentifier';
import { isOwner } from '@/lib/rbac';
import { useUserLogged } from './useUserLogged';

export type LeaderboardType = 'engineers' | 'teams' | 'companies';

interface TeamsLeaderboardResponse {
  teams: Array<{
    team_uuid: string;
    engineers: Array<{
      engineer: string;
      total_impact_score: number;
      streak: number;
    }>;
  }>;
  total_engineers: number | null;
  error: string | null;
}

export type LeaderboardResponse =
  | EngineerLeaderboardResponse
  | OrganizationLeaderboardResponse
  | TeamsLeaderboardResponse;

/**
 * URL param carrying the "Paid seats only" filter, written by
 * <PaidSeatsFilter /> and read here. Kept in the URL rather than passed down as
 * a prop because the leaderboard page renders three independent callers of this
 * hook (the board, Champions, the Statusbar) plus a Share button: reading one
 * param means they always agree, and a shared link carries the filter.
 */
export const SEATS_ONLY_PARAM = 'seats';
export const SEATS_ONLY_VALUE = 'paid';

/**
 * The single answer to "is the paid-seats filter on for this request?".
 *
 * Four hooks need it (`useLeaderboard`, `useUserGlobalRank`, `useTags`), and
 * they must agree — a board filtered while its rank card is not is the bug this
 * whole feature is trying to avoid. Exported and pure so it can be tested
 * directly: an inlined copy that silently ignored `followSeatsFilter` shipped
 * once already, and neither tsc nor eslint can see that mistake.
 *
 * @param orgUUID Required for the filter to mean anything: the global board has
 *   no roster to filter against, and the backend ignores the flag without it.
 * @param seatsParam The raw `?seats=` value.
 * @param followSeatsFilter False where the rank is shown OUTSIDE the filtered
 *   board (ShareButton's card links to the unfiltered global leaderboard).
 */
export const resolveSeatsOnly = ({
  orgUUID,
  seatsParam,
  followSeatsFilter = true,
}: {
  orgUUID?: string | null;
  seatsParam?: string | null;
  followSeatsFilter?: boolean;
}): boolean =>
  Boolean(followSeatsFilter && orgUUID && seatsParam === SEATS_ONLY_VALUE);

/**
 * URL param carrying the "My team" / "Whole org" leaderboard scope toggle
 * (rbac-plan.md B18), written by <LeaderboardScopeToggle /> and read here.
 * "team" is the default for every role except an Owner: the backend ignores
 * `scope` for them and always returns org-wide rows, so the FE must not
 * select "My team" over data the response never actually filtered.
 */
export const SCOPE_PARAM = 'scope';
export const SCOPE_TEAM = 'team';
export const SCOPE_ORG = 'org';
export type LeaderboardScope = typeof SCOPE_TEAM | typeof SCOPE_ORG;

/** Org-scoped boards only: the global leaderboard has no team to scope by. */
export const resolveLeaderboardScope = ({
  orgUUID,
  scopeParam,
  viewerRole,
}: {
  orgUUID?: string | null;
  scopeParam?: string | null;
  viewerRole?: string | null;
}): LeaderboardScope => {
  if (!orgUUID) return SCOPE_TEAM;
  if (isOwner(viewerRole)) return SCOPE_ORG;
  return scopeParam === SCOPE_ORG ? SCOPE_ORG : SCOPE_TEAM;
};

export const useLeaderboard = (
  type: LeaderboardType,
  timeRange: string = 'weekly',
  orgUUID?: string,
  teamsUUID?: string[]
) => {
  const { organization, user } = useUserLogged();
  const searchParams = useSearchParams();
  const normalizedOrgUUID = normalizeOrgUUID(orgUUID);
  // Org-scoped boards only, so the global leaderboard neither filters nor
  // caches two identical responses under different keys.
  const seatsOnly = resolveSeatsOnly({
    orgUUID: normalizedOrgUUID,
    seatsParam: searchParams?.get(SEATS_ONLY_PARAM),
  });
  const scope = resolveLeaderboardScope({
    orgUUID: normalizedOrgUUID,
    scopeParam: searchParams?.get(SCOPE_PARAM),
    viewerRole: user?.Role,
  });
  const query = useQuery<LeaderboardResponse>({
    queryKey: [
      'leaderboard',
      type,
      timeRange,
      normalizedOrgUUID,
      teamsUUID,
      seatsOnly,
      scope,
    ],
    queryFn: (): Promise<LeaderboardResponse> =>
      fetchLeaderboard(
        type,
        timeRange,
        normalizedOrgUUID,
        teamsUUID,
        seatsOnly,
        scope
      ),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
    retry: 2,
  });

  return query;
};

const fetchLeaderboard = async (
  type: LeaderboardType,
  timeRange: string = 'weekly',
  orgUUID?: string,
  teamsUUID?: string[],
  seatsOnly: boolean = false,
  scope: LeaderboardScope = SCOPE_TEAM
) => {
  if (orgUUID) {
    if (type === 'engineers') {
      return fetchEngineerLeaderboardByOrg(timeRange, orgUUID, seatsOnly, scope);
    } else if (type === 'teams') {
      return fetchOrganizationTeamsLeaderboard(
        timeRange,
        orgUUID,
        teamsUUID as string[],
        seatsOnly
      );
    } else {
      return fetchOrganizationLeaderboard(timeRange, orgUUID);
    }
  } else {
    if (type === 'engineers') {
      return fetchEngineerLeaderboard(timeRange);
    } else {
      return fetchOrganizationLeaderboard(timeRange, undefined);
    }
  }
};
